import React, { useEffect, useState, useRef } from "react";
import { Button, Typography, Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { subdomain, getBackendUrl } from "../../auth/utilities";
import cookie from "react-cookies";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import CircularProgress from "@mui/material/CircularProgress";
import { useLocation } from "react-router-dom";
import "./TrainingVideoAssessment.css";

export default function TrainingVideoAssessment() {
    const { id } = useParams();
    const [training, setTraining] = useState(null);
    const [sections, setSections] = useState([]);
    const [activeVideo, setActiveVideo] = useState(null);
    const csrfToken = cookie.load("csrftoken");
    const [completedSections, setCompletedSections] = useState([]);
    const [isAssessmentMode, setIsAssessmentMode] = useState(false);
    const navigate = useNavigate();
    const [assessmentButtonLoading, setAssessmentButtonLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [certificateLoading, setCertificateLoading] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(null);
    const backend = getBackendUrl();
    const [answers, setAnswers] = useState({});
    const [certificateUrl, setCertificateUrl] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [assessmentErrorMessage, setAssessmentErrorMessage] = useState(null);
    const assessmentErrorTimerRef = useRef(null);
    const [trainingError, setTrainingError] = useState(null);

    const location = useLocation();
    const trainingStatus = location.state?.training_status;

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

    const allCompleted = sections.length > 0 && completedSections.length === sections.length;
    const allAnswered = questions.length > 0 && Object.keys(answers).length === questions.length;

    // Show error popup that auto-dismisses
    const showAssessmentErrorAndReturnToVideo = (text) => {
        // Reset all assessment state for fresh attempt
        setAnswers({});           // Clear all answers
        setQuestions([]);         // Clear questions
        setCompletedSections([]); // Reset video completion tracking
        setActiveVideo(sections[0]); // Reset to first video
        setAssessmentErrorMessage(text);
        setIsAssessmentMode(false);
        
        if (assessmentErrorTimerRef.current) {
            clearTimeout(assessmentErrorTimerRef.current);
        }
        
        assessmentErrorTimerRef.current = setTimeout(() => {
            setAssessmentErrorMessage(null);
        }, 3000);
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (assessmentErrorTimerRef.current) {
                clearTimeout(assessmentErrorTimerRef.current);
            }
        };
    }, []);

    useEffect(() => { fetchTrainingSections(); }, []);

    const fetchTrainingSections = async () => {
        try {
            const response = await fetch(
                `${backend}${subdomain}/${cookie.load("Language") || "en"}/trainingmodule/trainingsection/${id}/`,
                { method: "GET", credentials: "include", headers: { "Content-Type": "application/json" } }
            );
            const data = await response.json();
            if (data.status === "success") {
                setTraining(data.training);
                setSections(data.sections);
                setActiveVideo(data.sections[0]);
                setTrainingError(null);
            } else if (data.status === "error" || data.error) {
                setTrainingError(data.message || data.error || "Failed to load training sections.");
                setSnackbar({ open: true, message: data.message || data.error || "Failed to load training sections.", severity: "error" });
            }
        } catch (error) {
            console.error("Error fetching training sections", error);
            setTrainingError("An error occurred while fetching training sections.");
            setSnackbar({ open: true, message: "An error occurred while fetching training sections.", severity: "error" });
        }
    };

    const handleLogout = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${backend}${subdomain}/en/trainingmodule/logouttraining/`,
                { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken } }
            );
            const data = await response.json();
            if (!response.ok) { console.log("Logout error:", data?.error || "Logout failed"); return; }
            if (data?.message) {
                localStorage.removeItem("training_token");
                navigate("/whizz-learning/");
            }
        } catch (error) {
            navigate("/whizz-learning/");
        } finally {
            setLoading(false);
        }
    };

    const handleVideoCompleted = (id) => {
        if (!sections?.length) return;
        if (!completedSections.includes(id)) {
            setCompletedSections(prev => [...prev, id]);
        }
        const currentIndex = sections.findIndex(section => section.id === id);
        if (currentIndex !== -1 && currentIndex < sections.length - 1) {
            setIsVideoLoading(true);
            setTimeout(() => { setActiveVideo(sections[currentIndex + 1]); }, 5000);
        }
    };

    const fetchAssessmentQuestions = async () => {
        try {
            setAssessmentButtonLoading(true);
            setLoadingQuestions(true);
            const response = await fetch(
                `${backend}${subdomain}/${cookie.load("Language") || "en"}/trainingmodule/trainingquestion/${id}/`,
                { method: "GET", credentials: "include", headers: { "Content-Type": "application/json" } }
            );
            const data = await response.json();
            if (data.status === "error" || data.error) {
                setSnackbar({ open: true, message: data.message || data.error || "Failed to load questions.", severity: "error" });
                return;
            }
            setQuestions(data.questions || []);
            setIsAssessmentMode(true);
        } catch (error) {
            console.error("Error loading assessment questions:", error);
            setSnackbar({ open: true, message: "An error occurred while loading questions.", severity: "error" });
        } finally {
            setLoadingQuestions(false);
            setAssessmentButtonLoading(false);
        }
    };

    const downloadCertificate = async (trainingId) => {
        if (!trainingId) return;
        setLoadingDownload(trainingId);
        try {
            const language = cookie.load("Language") || "en";
            const apiUrl = `${backend}${subdomain}/${language}/trainingmodule/getcertificate/${trainingId}/`;
            const res = await fetch(apiUrl, { credentials: "include" });
            if (!res.ok) throw new Error("Failed to fetch certificate URL");
            const data = await res.json();
            const fileUrl = data.certificate_url;
            window.location.href = fileUrl;
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setLoadingDownload(null);
        }
    };

    const submitAssessment = async () => {
        try {
            setSubmitLoading(true);
            const questions_data = Object.keys(answers).map((qid) => ({ id: parseInt(qid), selected_answer: answers[qid] }));
            const response = await fetch(
                `${backend}${subdomain}/${cookie.load("Language") || "en"}/trainingmodule/submittraining/${id}/`,
                { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ questions_data }) }
            );
            const data = await response.json();
            if (data.status === "failed") {
                const errorMessage = data.message || "A minimum of seven correct answers is required to pass the assessment.";
                
                // Always redirect to video section on assessment failure
                showAssessmentErrorAndReturnToVideo(errorMessage);
                return;
            }
            if (data.status !== "success") {
                setSnackbar({ open: true, message: "Something went wrong. Please try again.", severity: "error" });
                return;
            }
            setCertificateLoading(true);
            setIsAssessmentMode(false);
            const certRes = await fetch(
                `${backend}${subdomain}/${cookie.load("Language") || "en"}/trainingmodule/getcertificate/${id}/`,
                { method: "GET", credentials: "include", headers: { "Content-Type": "application/json" } }
            );
            const certData = await certRes.json();
            setCertificateLoading(false);
            if (certData.status === "success" && certData.certificate_url) {
                setCertificateUrl(certData.certificate_url);
            } else {
                setSnackbar({ open: true, message: "Certificate generation failed. Please try again.", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Server error. Try again.", severity: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="whizz-learning-assessment-root">
            {/* NAVBAR */}
            <Box className="training-navbar" style={{ position: "sticky", top: 0, zIndex: 1000, width: "100%", background: "#000034", padding: "12px 25px", borderRadius: "7px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => navigate("/whizz-learning/training-list/")}>
                    <ArrowBackIcon style={{ color: "white", fontSize: "22px", marginRight: "10px" }} />
                    <h3 style={{ color: "white", margin: 0, fontFamily: "lato", fontSize: "18px", fontWeight: 700 }}>
                        {certificateLoading || certificateUrl ? "Certificate" : isAssessmentMode ? "Training Assessment" : "Training List"}
                    </h3>
                </div>
                <Button className="navbar-logout-btn" onClick={handleLogout} disabled={loading} style={{ background: "white", color: "#000034", fontSize: "12px", padding: "6px 14px", borderRadius: "3px", textTransform: "none", fontFamily: "lato", fontWeight: 600, opacity: loading ? 0.7 : 1 }}>
                    {loading ? "Logging out..." : "Logout"}
                </Button>
            </Box>

            <div className="assessment-root">
                {certificateLoading || certificateUrl ? (
                    /* CERTIFICATE MODE */
                    <div className="certificate-mode-container">
                        {!certificateLoading && certificateUrl && (
                            <>
                                {[...Array(30)].map((_, i) => (
                                    <div key={i} className="confetti-particle" style={{ width: `${8 + Math.random() * 8}px`, height: `${8 + Math.random() * 8}px`, background: ["#FFD700", "#FF69B4", "#00BFFF", "#32CD32", "#FFA500", "#FF6347", "#9370DB"][i % 7], borderRadius: Math.random() > 0.5 ? "50%" : "0%", top: `${Math.random() * -10}%`, left: `${Math.random() * 100}%`, animation: `confetti ${4 + Math.random() * 4}s linear infinite`, animationDelay: `${Math.random() * 3}s`, transform: `rotate(${Math.random() * 360}deg)` }} />
                                ))}
                            </>
                        )}
                        {certificateLoading && (
                            <>
                                <div className="loading-spinner"></div>
                                <Typography className="generating-text">Generating your certificate, please wait...</Typography>
                            </>
                        )}
                        {!certificateLoading && certificateUrl && (
                            <div style={{ zIndex: 2, width: "100%" }}>
                                <div className="congrats-header-box">
                                    <Typography className="congrats-title">
                                        🎉 Congratulations! Your Certificate is Ready 🎓
                                    </Typography>
                                    <Button onClick={() => downloadCertificate(id)} variant="contained" disabled={loadingDownload === id}
                                        startIcon={loadingDownload === id ? <CircularProgress size={20} color="inherit" /> : <DownloadForOfflineIcon style={{ fontSize: "23px" }} />}
                                        sx={{ background: "#000034", color: "white", borderRadius: "50px", textTransform: "none", fontFamily: "lato", fontSize: "14px", fontWeight: "700", boxShadow: "0 8px 20px rgba(0,0,52,0.3)", ml: 2, whiteSpace: "nowrap" }}>
                                        {loadingDownload === id ? "Downloading..." : "Download Certificate"}
                                    </Button>
                                </div>
                                <div className="cert-img-wrapper">
                                    <img src={certificateUrl} alt="Certificate" className="final-cert-image" />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* VIDEO / ASSESSMENT MODE */
                    <>
                        <div className="assessment-content-area">
                            {isAssessmentMode ? (
                                <>
                                    <Typography className="assessment-heading">Assessment Questions</Typography>
                                    {loadingQuestions ? (
                                        <div style={{ textAlign: "center", padding: "20px" }}>
                                            <div className="loading-spinner" style={{ margin: "0 auto 12px" }}></div>
                                            <Typography>Loading questions…</Typography>
                                        </div>
                                    ) : (
                                        questions.map((q, index) => (
                                            <div key={q.id} className="question-box">
                                                <p className="question-text">{index + 1}. {q.questionDescription}</p>
                                                {q.options.filter((opt) => opt !== null).map((opt, i) => (
                                                    <label key={i} className={`option-label ${answers[q.id] === opt ? "selected" : "unselected"}`}>
                                                        <input type="radio" name={`question-${q.id}`} value={opt} checked={answers[q.id] === opt} onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt }))} className="option-radio" />
                                                        <span>{opt}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        ))
                                    )}
                                    <Button variant="contained" disabled={submitLoading || !allAnswered} onClick={submitAssessment}
                                        className="submit-assessment-btn"
                                        style={{ background: submitLoading || !allAnswered ? "#777" : "#000034" }}>
                                        {submitLoading ? "Submitting..." : "Submit Assessment"}
                                    </Button>
                                </>
                            ) : (
                                /* VIDEO MODE */
                                <>
                                    {!training ? (
                                        trainingError ? (
                                            <div className="video-loader-box">
                                                <Typography color="error">{trainingError}</Typography>
                                            </div>
                                        ) : (
                                            <div className="video-loader-box">
                                                <div className="loading-spinner" style={{ margin: "0 auto 12px" }}></div>
                                                <Typography>Loading training…</Typography>
                                            </div>
                                        )
                                    ) : (
                                        <Typography className="video-title-heading">{training.name}</Typography>
                                    )}
                                    {activeVideo && (
                                        <div className="video-player-wrapper">
                                            <video id="trainingVideo" key="training-video" src={activeVideo?.url} controls autoPlay controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} onEnded={() => handleVideoCompleted(activeVideo.id)} onLoadedData={() => setIsVideoLoading(false)} className="video-element" />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* RIGHT SIDEBAR */}
                        {!isAssessmentMode && !certificateUrl && !certificateLoading && (
                            <div className="assessment-sidebar">
                                <div className="sidebar-scroll-area">
                                    <h3 className="sidebar-heading">All Training Videos</h3>
                                    {sections.map((section) => (
                                        <div key={section.id} className="video-item-row" onClick={() => setActiveVideo(section)}>
                                            <video src={section.url} muted playsInline className="video-thumbnail-sidebar" />
                                            <div>
                                                <b style={{ fontSize: "13px" }}>{section.name}</b>
                                                <div style={{ fontSize: "12px", color: "#666" }}>🎬 Video</div>
                                                {trainingStatus?.trim().toLowerCase() === "completed" || completedSections.includes(section.id) ? (
                                                    <span className="video-status-completed">✔ Completed</span>
                                                ) : (
                                                    <span className="video-status-pending">✘ Not Completed</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {trainingStatus?.trim().toLowerCase() !== "completed" && (
                                    <Button variant="contained" disabled={!allCompleted || assessmentButtonLoading} onClick={fetchAssessmentQuestions}
                                        className="take-assessment-btn"
                                        style={{ background: allCompleted ? "#000034" : "#9ca3af", cursor: allCompleted ? "pointer" : "not-allowed", opacity: allCompleted ? 1 : 0.6 }}>
                                        {assessmentButtonLoading ? "Loading…" : "Take the Assessment"}
                                    </Button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Assessment Error Popup - Auto Dismiss */}
            {assessmentErrorMessage && (
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        background: "white",
                        borderRadius: "12px",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                        padding: "24px",
                        textAlign: "center",
                        maxWidth: "400px",
                        animation: "fadeIn 0.3s ease-in-out",
                    }}
                >
                    <style>
                        {`
                            @keyframes fadeIn {
                                from {
                                    opacity: 0;
                                    transform: translate(-50%, -48%);
                                }
                                to {
                                    opacity: 1;
                                    transform: translate(-50%, -50%);
                                }
                            }
                        `}
                    </style>
                    <div
                        style={{
                            fontSize: "48px",
                            marginBottom: "12px",
                        }}
                    >
                        ⚠️
                    </div>
                    <Typography
                        style={{
                            fontFamily: "lato",
                            fontWeight: "700",
                            fontSize: "16px",
                            color: "#000034",
                            marginBottom: "8px",
                        }}
                    >
                        Assessment Not Passed
                    </Typography>
                    <Typography
                        style={{
                            fontFamily: "lato",
                            fontSize: "14px",
                            color: "#666",
                        }}
                    >
                        {assessmentErrorMessage}
                    </Typography>
                </div>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "Top", horizontal: "center" }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
